import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { BrandingService } from './branding.service';
import { ConsentService } from './consent.service';
import { hasSensitiveUrlParam, urlHasSensitiveParam } from './sensitive-url-params';

/**
 * Strict id formats. We NEVER inject raw HTML — only ids that pass these
 * regexes are interpolated into the official vendor snippets.
 */
const GA4_ID_RE = /^G-[A-Z0-9]+$/;
const META_PIXEL_ID_RE = /^\d{15,16}$/;
const GTM_ID_RE = /^GTM-[A-Z0-9]+$/;

/**
 * Layout-less public routes that must NEVER be reported to analytics.
 * /gosc/:token and /guest/:token carry a secret guest-info token in the URL
 * path; /embed/ renders inside third-party iframes. App load on these paths is
 * already guarded in app.ts (IS_LAYOUTLESS_ROUTE skips injecting this service),
 * but this defense-in-depth filter also covers any FUTURE in-app navigation
 * landing there after analytics was initialized on a regular page.
 */
const UNTRACKED_URL_RE = /^\/(embed|gosc|guest)\//;

/** Minimal typings for the globals the vendor snippets create. */
type GtagFn = (...args: unknown[]) => void;
type FbqFn = (...args: unknown[]) => void;

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFn;
  fbq?: FbqFn;
}

/**
 * Injects owner-configured analytics (GA4 / Meta Pixel / GTM) on white-label
 * hosts, and ONLY after the visitor granted analytics consent.
 *
 * Hard gates (all must hold before anything is injected):
 *   1. `branding.isWhiteLabel()` — never runs on the main Zaklepuj host.
 *   2. `consent.analyticsGranted()` — RODO/GDPR opt-in.
 *   3. A validly-formatted id is present for the given vendor.
 *
 * Each vendor snippet is injected at most once per session. Consent granted
 * later in the session triggers injection then (effect re-evaluation). After
 * injection, Router NavigationEnd dispatches a SPA `page_view` to gtag/fbq.
 *
 * Token-leak ban: the vendor tags read `location.href` on load (and Meta Pixel
 * on history changes). While the URL carries a credential-like param (`t` /
 * `token`, see `sensitive-url-params.ts`) nothing is injected — re-checked on
 * every NavigationEnd — and no page_view is dispatched for such a URL.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly branding = inject(BrandingService);
  private readonly consent = inject(ConsentService);

  private ga4Injected = false;
  private metaInjected = false;
  private gtmInjected = false;
  private routerHooked = false;
  /** Consent + white-label hold, but the URL still carries a credential. */
  private pendingInject = false;

  /** Whether at least one tag is live (controls page_view dispatch). */
  private get anyInjected(): boolean {
    return this.ga4Injected || this.metaInjected || this.gtmInjected;
  }

  constructor() {
    // Re-evaluate whenever white-label state or analytics consent changes.
    effect(() => {
      const enabled = this.branding.isWhiteLabel() && this.consent.analyticsGranted();
      if (!enabled) {
        this.pendingInject = false;
        return;
      }
      if (this.urlHasCredential()) {
        this.pendingInject = true;
        this.hookRouter();
        return;
      }
      this.injectAll();
    });
  }

  /** `t=` / `token=` in the current address bar. */
  private urlHasCredential(): boolean {
    return hasSensitiveUrlParam(this.doc.defaultView?.location?.search);
  }

  private injectAll(): void {
    const ids = this.branding.analytics();
    if (!ids) {
      return;
    }

    const ga4 = ids.ga4MeasurementId?.trim();
    if (ga4 && GA4_ID_RE.test(ga4)) {
      this.injectGa4(ga4);
    }

    const meta = ids.metaPixelId?.trim();
    if (meta && META_PIXEL_ID_RE.test(meta)) {
      this.injectMetaPixel(meta);
    }

    const gtm = ids.gtmContainerId?.trim();
    if (gtm && GTM_ID_RE.test(gtm)) {
      this.injectGtm(gtm);
    }

    if (this.anyInjected) {
      this.hookRouter();
    }
  }

  // ── GA4 (gtag.js) ──────────────────────────────────────────────────────────

  private injectGa4(id: string): void {
    if (this.ga4Injected) {
      return;
    }
    const win = this.doc.defaultView as AnalyticsWindow | null;
    if (!win) {
      return;
    }

    const loader = this.doc.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    this.doc.head.appendChild(loader);

    win.dataLayer = win.dataLayer || [];
    const gtag: GtagFn = (...args: unknown[]) => {
      win.dataLayer?.push(args);
    };
    win.gtag = gtag;
    gtag('js', new Date());
    // SPA: we send page_view manually on NavigationEnd.
    gtag('config', id, { send_page_view: false });

    this.ga4Injected = true;
  }

  // ── Meta Pixel (fbq) ───────────────────────────────────────────────────────

  private injectMetaPixel(id: string): void {
    if (this.metaInjected) {
      return;
    }
    const win = this.doc.defaultView as AnalyticsWindow | null;
    if (!win) {
      return;
    }

    // Official Meta Pixel bootstrap, reimplemented in typed code (no raw HTML).
    if (!win.fbq) {
      const queue: unknown[][] = [];
      const fbq: FbqFn = (...args: unknown[]) => {
        const f = win.fbq as (FbqFn & { callMethod?: GtagFn }) | undefined;
        if (f?.callMethod) {
          f.callMethod(...args);
        } else {
          queue.push(args);
        }
      };
      const fbqWithProps = fbq as FbqFn & {
        queue: unknown[][];
        loaded: boolean;
        version: string;
      };
      fbqWithProps.queue = queue;
      fbqWithProps.loaded = true;
      fbqWithProps.version = '2.0';
      win.fbq = fbqWithProps;

      const loader = this.doc.createElement('script');
      loader.async = true;
      loader.src = 'https://connect.facebook.net/en_US/fbevents.js';
      this.doc.head.appendChild(loader);
    }

    win.fbq?.('init', id);
    win.fbq?.('track', 'PageView');

    this.metaInjected = true;
  }

  // ── Google Tag Manager ─────────────────────────────────────────────────────

  private injectGtm(id: string): void {
    if (this.gtmInjected) {
      return;
    }
    const win = this.doc.defaultView as AnalyticsWindow | null;
    if (!win) {
      return;
    }

    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const loader = this.doc.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    this.doc.head.appendChild(loader);

    this.gtmInjected = true;
  }

  // ── SPA page_view dispatch ─────────────────────────────────────────────────

  private hookRouter(): void {
    if (this.routerHooked) {
      return;
    }
    this.routerHooked = true;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (this.pendingInject) {
          if (this.urlHasCredential()) {
            return;
          }
          this.pendingInject = false;
          this.injectAll();
        }
        if (!this.anyInjected) {
          return;
        }
        // Token-leak ban: never report secret-token / embed destinations.
        if (
          UNTRACKED_URL_RE.test(e.urlAfterRedirects) ||
          urlHasSensitiveParam(e.urlAfterRedirects) ||
          this.urlHasCredential()
        ) {
          return;
        }
        this.trackPageView(e.urlAfterRedirects);
      });
  }

  private trackPageView(url: string): void {
    const win = this.doc.defaultView as AnalyticsWindow | null;
    if (!win) {
      return;
    }
    if (this.ga4Injected) {
      win.gtag?.('event', 'page_view', { page_path: url, page_location: win.location?.href });
    }
    if (this.metaInjected) {
      win.fbq?.('track', 'PageView');
    }
    // GTM picks up history changes via dataLayer; push an explicit event too.
    if (this.gtmInjected) {
      win.dataLayer?.push({ event: 'page_view', page_path: url });
    }
  }
}
